import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import { ThemeProvider } from "@mui/material/styles";
import AppStarLogo from "../images/appstar_title_tr.png";
import FormControl from "@mui/material/FormControl";
import { IconButton, InputAdornment, Alert } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import axios from "axios";
import { serverDomain, siteKey } from "./config";
import { useTheme } from "@mui/material/styles";
import RecaptchaEnterprise from "../components/RecaptchaEnterprise";
import Copyright from "../components/Copyright";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import { persistProfileState } from "../components/profileIconUtils";

// Set default values for the form fields
const defaultValues = {
  username: "",
  password: "",
  token: "",
};

export default function Login() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const loggedOut = location.state && location.state.loggedOut;
  const [formValues, setFormValues] = React.useState(defaultValues);
  const [errors, setErrors] = React.useState({});
  const [showPassword, setShowPassword] = React.useState(false);
  const [generalError, setGeneralError] = React.useState("");
  const recaptchaRef = React.useRef(null);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  // reCAPTCHA handlers
  const handleRecaptchaSuccess = React.useCallback((token) => {
    setFormValues((f) => ({ ...f, token }));
    setErrors((e) => ({ ...e, invalidRecaptcha: "" }));
    setGeneralError("");
  }, []);

  const handleRecaptchaExpired = React.useCallback(() => {
    setFormValues((f) => ({ ...f, token: "" }));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setGeneralError("");

    if (validate()) {
      // Log params being sent to server
      console.log("Submitting login with params:", {
        username: formValues.username,
        password: formValues.password,
        reCAPTCHA_token: formValues.token,
      });

      axios
        .post(serverDomain + "login.php", null, {
          params: {
            username: formValues.username,
            password: formValues.password,
            reCAPTCHA_token: formValues.token,
          },
        })
        .then((response) => {
          if (response.data.success) {
            console.log(response.data.message);
            // Handle successful login (e.g., save token, redirect)
            console.log("login success:", response);
            const { token, user } = response.data;
            const isAdmin = !!(user && (user.is_admin === true || user.is_admin === 1 || user.type === "admin"));
            localStorage.setItem("appstar_jwt", token);
            localStorage.setItem("appstar_admin", isAdmin ? "true" : "false");
            if (user && user.user_id !== undefined && user.user_id !== null) {
              localStorage.setItem("appstar_user_id", String(user.user_id));
            }
              if (user && user.username !== undefined && user.username !== null) {
                localStorage.setItem("appstar_username", String(user.username));
              }
            if (user && user.first_name !== undefined && user.first_name !== null) {
              localStorage.setItem("appstar_first_name", String(user.first_name));
            }
            if (user && user.last_name !== undefined && user.last_name !== null) {
              localStorage.setItem("appstar_last_name", String(user.last_name));
            }
            if (user && user.email !== undefined && user.email !== null) {
              localStorage.setItem("appstar_email", String(user.email));
            }
            persistProfileState({
              username: user?.username,
              first_name: user?.first_name,
              last_name: user?.last_name,
              email: user?.email,
              profile_icon_hash: user?.profile_icon_hash || ''
            });
            console.log("token saved to localStorage:", token);
            console.log("appstar_admin saved to localStorage:", localStorage.getItem("appstar_admin"));
            console.log("user_id saved to localStorage:", localStorage.getItem("appstar_user_id"));
            //   "token from local storage:",
            //   localStorage.getItem("token")
            // );
            navigate("/");
          } else {
            console.log("Login failed", response);

            // Reset reCAPTCHA
            if (recaptchaRef.current && recaptchaRef.current.reset) {
              recaptchaRef.current.reset();
            }

            setFormValues({
              ...formValues,
              token: "",
            });

            console.log("Login error message", response.data.message);
            const errorMessage = response.data.message;

            // Clear all previous errors before setting new ones
            const newErrors = {};

            // Check for specific error types
            if (errorMessage.includes("reCAPTCHA")) {
              newErrors.invalidRecaptcha = errorMessage;
            } else if (errorMessage.includes("username")) {
              newErrors.username = errorMessage;
            } else if (errorMessage.includes("password")) {
              newErrors.password = errorMessage;
            } else {
              // General error
              setGeneralError(errorMessage);
            }

            setErrors(newErrors);
          }
        })
        .catch((error) => {
          console.log("Error", error);
          setGeneralError("An error occurred. Please try again.");
          
          // Reset reCAPTCHA on error
          if (recaptchaRef.current && recaptchaRef.current.reset) {
            recaptchaRef.current.reset();
          }
          
          setFormValues({
            ...formValues,
            token: "",
          });
        });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
    setGeneralError("");
  };

  const validate = () => {
    console.log("inside validate..");
    const temp = {};
    temp.username = formValues.username ? "" : "Username is required";
    temp.password = formValues.password ? "" : "Password is required";

    console.log("temp:", { ...temp });
    setErrors({
      ...temp,
    });
    return Object.values(temp).every((x) => x === "");
  };

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src={AppStarLogo}
            alt="AppStar Logo"
            style={{ marginBottom: 12, width: 100 }}
          />
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000'
            }}
          >
            AppStar.ai
          </Typography>
          <Typography component="h1" variant="h5">
            Sign in
          </Typography>

          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{ mt: 3, width: "100%" }}
          >
            {loggedOut && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  backgroundColor: '#d1fae5',
                  border: '1px solid #065f46',
                  borderRadius: 1,
                  color: '#065f46',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  textAlign: 'center'
                }}
              >
                You are now logged out
              </Box>
            )}
            {generalError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {generalError}
              </Alert>
            )}

            <Grid item xs={12} sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <TextField
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  value={formValues.username}
                  onChange={handleInputChange}
                  error={!!errors.username}
                  helperText={errors.username}
                  variant="outlined"
                  InputLabelProps={{
                    sx: (t) => ({
                      backgroundColor: t.palette.background.default,
                      px: 0.5,
                    })
                  }}
                  sx={{}}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  value={formValues.password}
                  onChange={handleInputChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  variant="outlined"
                  InputLabelProps={{
                    sx: (t) => ({
                      backgroundColor: t.palette.background.default,
                      px: 0.5,
                    })
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                      style: {
                        WebkitTextSecurity: showPassword ? "none" : "disc",
                      },
                    },
                  }}
                  inputProps={{
                    style: {
                      WebkitTextSecurity: showPassword ? "none" : "disc",
                    },
                  }}
                  sx={{
                    "& input::-ms-reveal": {
                      display: "none",
                    },
                    "& input::-ms-clear": {
                      display: "none",
                    },
                  }}
                />
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ mt: 2, display: "flex", justifyContent: "center" }}
            >
              <RecaptchaEnterprise
                ref={recaptchaRef}
                siteKey={siteKey}
                onSuccess={handleRecaptchaSuccess}
                onExpired={handleRecaptchaExpired}
                error={errors.invalidRecaptcha}
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 1,
                  mb: 2,
                  backgroundColor: 'var(--dark-blue)',
                  color: '#fff',
                  padding: '0.75rem',
                  "&:hover": {
                    opacity: 0.9,
                  },
                }}
                disabled={!formValues.token}
              >
                Sign In
              </Button>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Link component={RouterLink} to="/signup" variant="body2">
                Don't have an account? Sign up
              </Link>
            </Grid>
          </Box>
        </Box>

        <Copyright sx={{ mt: 5, mb: 2 }} />
      </Container>
    </ThemeProvider>
  );
}
